import React, { useState, useEffect } from 'react';
import { getIndividualServicesList, getServicesList, updateTherapistServices, getTherapistDetails } from '../../../../APICalling/APICalling';
import { toast } from 'react-toastify';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import Loader from '../../../../Components/Loader/Loader';
import styles from './Services.module.css';

const Services = ({ therapistId }) => {
    const { selectedOutlet, loggedOutletID } = useGlobalContext();
    const [services, setServices] = useState([]);
    const [categories, setCategories] = useState({});
    const [categoriesLoaded, setCategoriesLoaded] = useState(false);
    const [therapistServices, setTherapistServices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [updatingServiceId, setUpdatingServiceId] = useState(null); // Track which service is being updated
    const [updatingCategory, setUpdatingCategory] = useState(null); // Track which category is being updated

    useEffect(() => {
        const loadData = async () => {
            setCategoriesLoaded(false);
            await fetchCategories();
        };
        loadData();
    }, [selectedOutlet]);

    useEffect(() => {
        if (categoriesLoaded && Object.keys(categories).length > 0 && therapistId) {
            fetchTherapistData();
        }
    }, [therapistId, selectedOutlet, categories, categoriesLoaded]);

    const fetchTherapistData = async (showLoader = true) => {
        if (showLoader) {
            setLoading(true);
        }
        try {
            // Fetch therapist details to get assigned services
            const therapistResponse = await getTherapistDetails(therapistId);
            
            if (therapistResponse?.data?.success) {
                const assignedServices = therapistResponse.data.data?.services || [];
                setTherapistServices(assignedServices);
                
                // Now fetch all available services
                await fetchServices(assignedServices, showLoader);
            } else {
                setError('Failed to load therapist data');
                toast.error('Failed to load therapist data');
                if (showLoader) {
                    setLoading(false);
                }
            }
        } catch (err) {
            console.error('Error fetching therapist data:', err);
            setError('Error loading therapist data');
            toast.error('Error loading therapist data');
            if (showLoader) {
                setLoading(false);
            }
        }
    };

    const fetchCategories = async () => {
        try {
            const params = {
                pagination: 0,
                status: 1,
                outlet: selectedOutlet || loggedOutletID
            };

            const response = await getServicesList(params);
            
            if (response?.data?.success) {
                const categoriesData = response.data.data?.list?.category || [];
                // Create a mapping of category ID to category data (including name and sort)
                const categoryMap = {};
                categoriesData.forEach(category => {
                    if (category.id && category.name) {
                        categoryMap[category.id] = {
                            name: category.name,
                            sort: category.sort || 999 // Default high value if sort is missing
                        };
                    }
                });
                setCategories(categoryMap);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
        } finally {
            setCategoriesLoaded(true);
        }
    };

    const fetchServices = async (assignedServices, showLoader = true) => {
        setError(null);
        
        try {
            const params = {
                pagination: 0,
                outlet: selectedOutlet || loggedOutletID,
                status: 1
            };

            const response = await getIndividualServicesList(params);
            
            if (response?.data?.success) {
                const servicesData = response.data.data?.list?.service || [];
                
                // Create a Set of assigned service IDs for quick lookup
                const assignedServiceIds = new Set(
                    assignedServices.map(service => service.service_id)
                );
                
                // Map services and set status based on therapist's assigned services
                const servicesWithStatus = servicesData.map(service => ({
                    ...service,
                    status: assignedServiceIds.has(service.id) ? 1 : 0
                }));
                
                // Group services by category
                const groupedServices = groupServicesByCategory(servicesWithStatus);
                setServices(groupedServices);
            } else {
                setError('Failed to load services');
                toast.error('Failed to load services');
            }
        } catch (err) {
            console.error('Error fetching services:', err);
            setError('Error loading services');
            toast.error('Error loading services');
        } finally {
            if (showLoader) {
                setLoading(false);
            }
        }
    };

    const groupServicesByCategory = (servicesArray) => {
        const categoryMap = {};
        
        servicesArray.forEach(service => {
            const categoryId = service.service_category_id;
            
            if (!categoryMap[categoryId]) {
                categoryMap[categoryId] = {
                    id: categoryId,
                    name: getCategoryName(categoryId),
                    sort: getCategorySort(categoryId),
                    services: []
                };
            }
            
            categoryMap[categoryId].services.push(service);
        });
        
        // Convert to array and sort by the 'sort' field
        return Object.values(categoryMap).sort((a, b) => a.sort - b.sort);
    };

    const getCategoryName = (categoryId) => {
        return categories[categoryId]?.name || `Category ${categoryId}`;
    };

    const getCategorySort = (categoryId) => {
        return categories[categoryId]?.sort || 999; // Default high value if sort is missing
    };

    const isUpdatingAnyService = updatingServiceId !== null || updatingCategory !== null;

    const handleToggleChange = async (toggledService) => {
        if (!therapistId) {
            toast.error('Therapist ID is required');
            return;
        }

        // Prevent concurrent updates while a service/category request is in flight
        if (isUpdatingAnyService) {
            return;
        }

        const newStatus = toggledService.status === 1 ? 0 : 1;
        
        // Calculate services after the toggle
        const allServices = [];
        services.forEach(category => {
            category.services.forEach(service => {
                if (service.id === toggledService.id) {
                    if (newStatus === 1) {
                        allServices.push(service.id);
                    }
                } else {
                    if (service.status === 1) {
                        allServices.push(service.id);
                    }
                }
            });
        });

        // Check if at least one service will remain
        // if (allServices.length === 0) {
        //     toast.error('At least one service must be enabled for the therapist');
        //     return;
        // }

        // Optimistically update UI immediately
        const updatedServices = services.map(category => ({
            ...category,
            services: category.services.map(service => 
                service.id === toggledService.id 
                    ? { ...service, status: newStatus }
                    : service
            )
        }));
        setServices(updatedServices);

        try {
            setUpdatingServiceId(toggledService.id);

            const dataToPost = {
                services: allServices.join(','),
                outlet: selectedOutlet || loggedOutletID,
                therapist: therapistId,
            };

            const response = await updateTherapistServices(therapistId, dataToPost);
            
            if (response?.data?.success) {
                toast.success(`Service ${newStatus === 1 ? 'enabled' : 'disabled'} successfully`);
                // Refresh to ensure data consistency with server
                await fetchTherapistData(false);
            } else {
                // Revert the optimistic update on failure
                setServices(services);
                const errorMessage = response?.data?.message || 
                    (response?.data?.errors ? Object.values(response.data.errors).flat().join(', ') : 'Failed to update service status');
                toast.error(errorMessage);
            }
        } catch (err) {
            // Revert the optimistic update on error
            setServices(services);
            console.error('Error updating service status:', err);
            toast.error('Error updating service status');
        } finally {
            setUpdatingServiceId(null);
        }
    };

    const handleEnableAll = async (categoryServices) => {
        if (!therapistId) {
            toast.error('Therapist ID is required');
            return;
        }

        // Get category ID to track updating state
        const categoryId = categoryServices[0]?.service_category_id;
        
        // Prevent concurrent updates while a service/category request is in flight
        if (isUpdatingAnyService) {
            return;
        }

        // Get IDs of services in the target category
        const categoryServiceIds = categoryServices.map(s => s.id);
        
        // Optimistically update UI immediately
        const updatedServices = services.map(category => ({
            ...category,
            services: category.services.map(service => 
                categoryServiceIds.includes(service.id) 
                    ? { ...service, status: 1 }
                    : service
            )
        }));
        setServices(updatedServices);

        try {
            setUpdatingCategory(categoryId);
            
            // Collect all services: enable all in target category, keep status of others
            const allServices = [];
            updatedServices.forEach(category => {
                category.services.forEach(service => {
                    if (service.status === 1) {
                        allServices.push(service.id);
                    }
                });
            });

            const dataToPost = {
                services: allServices.join(','),
                outlet: selectedOutlet || loggedOutletID,
                therapist: therapistId,
            };

            const response = await updateTherapistServices(therapistId, dataToPost);
            
            if (response?.data?.success) {
                toast.success('All services enabled successfully');
                await fetchTherapistData(false);
            } else {
                // Revert the optimistic update on failure
                setServices(services);
                const errorMessage = response?.data?.message || 
                    (response?.data?.errors ? Object.values(response.data.errors).flat().join(', ') : 'Failed to enable services');
                toast.error(errorMessage);
            }
        } catch (err) {
            // Revert the optimistic update on error
            setServices(services);
            console.error('Error enabling services:', err);
            toast.error('Error enabling services');
        } finally {
            setUpdatingCategory(null);
        }
    };

    const handleDisableAll = async (categoryServices) => {
        if (!therapistId) {
            toast.error('Therapist ID is required');
            return;
        }

        // Get category ID to track updating state
        const categoryId = categoryServices[0]?.service_category_id;
        
        // Prevent concurrent updates while a service/category request is in flight
        if (isUpdatingAnyService) {
            return;
        }

        // Get IDs of services in the target category
        const categoryServiceIds = categoryServices.map(s => s.id);

        // Check if at least one service will remain after disabling this category
        let remainingServices = 0;
        services.forEach(category => {
            category.services.forEach(service => {
                if (!categoryServiceIds.includes(service.id) && service.status === 1) {
                    remainingServices++;
                }
            });
        });

        // if (remainingServices === 0) {
        //     toast.error('At least one service must be enabled for the therapist');
        //     return;
        // }

        // Optimistically update UI immediately
        const updatedServices = services.map(category => ({
            ...category,
            services: category.services.map(service => 
                categoryServiceIds.includes(service.id) 
                    ? { ...service, status: 0 }
                    : service
            )
        }));
        setServices(updatedServices);

        try {
            setUpdatingCategory(categoryId);
            
            // Collect all services: disable all in target category, keep status of others
            const allServices = [];
            updatedServices.forEach(category => {
                category.services.forEach(service => {
                    if (service.status === 1) {
                        allServices.push(service.id);
                    }
                });
            });

            const dataToPost = {
                services: allServices.join(','),
                outlet: selectedOutlet || loggedOutletID,
                therapist: therapistId,
            };

            const response = await updateTherapistServices(therapistId, dataToPost);
            
            if (response?.data?.success) {
                toast.success('All services disabled successfully');
                await fetchTherapistData(false);
            } else {
                // Revert the optimistic update on failure
                setServices(services);
                const errorMessage = response?.data?.message || 
                    (response?.data?.errors ? Object.values(response.data.errors).flat().join(', ') : 'Failed to disable services');
                toast.error(errorMessage);
            }
        } catch (err) {
            // Revert the optimistic update on error
            setServices(services);
            console.error('Error disabling services:', err);
            toast.error('Error disabling services');
        } finally {
            setUpdatingCategory(null);
        }
    };

    // Show loader while requests are in progress
    if (loading || !categoriesLoaded || !therapistId) {
        return (
            <div className={styles.loaderContainer}>
                <Loader />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.servicesContainer}>
                <div className={styles.errorMessage}>
                    {error}
                </div>
            </div>
        );
    }

    if (Object.keys(categories).length === 0) {
        return (
            <div className={styles.servicesContainer}>
                <div className={styles.emptyState}>
                    Service categories not available
                </div>
            </div>
        );
    }

    if (!services || services.length === 0) {
        return (
            <div className={styles.servicesContainer}>
                <div className={styles.emptyState}>
                    Services not available
                </div>
            </div>
        );
    }

    return (
        <div className={styles.servicesContainer}>
            {services.map((category, categoryIndex) => {
                const categoryName = category.name || 'Uncategorized';
                const categoryServices = [...(category.services || [])].sort((a, b) => (parseInt(a.duration) || 0) - (parseInt(b.duration) || 0));
                
                // Check if all services in this category are enabled
                const allEnabled = categoryServices.every(service => service.status === 1);
                const allDisabled = categoryServices.every(service => service.status === 0);

                return (
                    <div key={categoryIndex} className={styles.categorySection}>
                        <div className={styles.categoryHeader}>
                            <h3 className={styles.categoryTitle}>{categoryName}</h3>
                            <button 
                                className={styles.toggleAllButton}
                                onClick={() => allEnabled ? handleDisableAll(categoryServices) : handleEnableAll(categoryServices)}
                                disabled={isUpdatingAnyService}
                            >
                                {allEnabled ? 'Disable all' : 'Enable all'}
                            </button>
                        </div>

                        <div className={styles.servicesTable}>
                            {/* Table Header */}
                            <div className={styles.tableHeader}>
                                <div className={styles.headerCell} style={{ flex: '1', minWidth: '200px' }}>Name</div>
                                <div className={styles.headerCell} style={{ flex: '0 0 150px', textAlign: 'left' }}>Duration</div>
                                <div className={styles.headerCell} style={{ flex: '0 0 80px', textAlign: 'right' }}>Status</div>
                            </div>

                            {/* Table Body */}
                            {categoryServices.length > 0 ? (
                                categoryServices.map((service, serviceIndex) => (
                                    <div key={serviceIndex} className={styles.tableRow}>
                                        <div className={styles.tableCell} style={{ flex: '1', minWidth: '200px' }}>
                                            {service.name || 'Unnamed Service'}
                                        </div>
                                        <div className={styles.tableCell} style={{ flex: '0 0 150px', textAlign: 'left' }}>
                                            {service.duration ? `${service.duration} min` : '-'}
                                        </div>
                                        <div className={styles.tableCell} style={{ flex: '0 0 80px', justifyContent: 'flex-end' }}>
                                            <label className={styles.switch}>
                                                <input
                                                    type="checkbox"
                                                    checked={service.status === 1}
                                                    onChange={() => handleToggleChange(service)}
                                                    disabled={isUpdatingAnyService}
                                                />
                                                <span className={styles.slider}></span>
                                            </label>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className={styles.emptyCategory}>
                                    No services in this category
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default Services;

